import React, { useRef } from 'react';
import LazyContext from '../../../LazyLoadingImage/LazyContext';
import { LAZYLOAD_ROW_START } from '../../../types';
import type {
  TorpedoItem,
  TorpedoLayoutedItem,
  TorpedoProps,
} from '../../types';
import { useRowVisibility } from '../hooks/useRowVisibility';
import TorpedoContentStrip from './ContentStrip';
import HiddenRow from './HiddenRow';
import ItemRow from './ItemRow';
import OffsetTracking from './OffsetTracking';
import TorpedoItemElement from './TorpedoItem';

export interface Props {
  idx: number;
  width: number;
  height: number;
  layoutItems: TorpedoLayoutedItem<TorpedoItem>[];
  dataItems: {
    [index: string]: TorpedoItem;
  };
  contentStrip?: React.ReactNode;
  scrollOptims?: boolean;
  onOffsetReached?: (offset: number) => void;
  rowAlign?: 'left' | 'center' | 'right';
  renderItem: (
    props: Partial<TorpedoProps>, // TODO: get rid of this one, noone needs it and causes unnecessary rerenders
    item: TorpedoLayoutedItem<TorpedoItem>,
    itemStyle: React.CSSProperties
  ) => React.ReactNode;
}

const TorpedoRow: React.FC<Props> = ({
  idx,
  width,
  height,
  layoutItems,
  dataItems,
  contentStrip,
  scrollOptims,
  onOffsetReached,
  renderItem,
  rowAlign,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useRowVisibility(ref, { enabled: scrollOptims });

  return (
    <LazyContext.Provider value={idx >= LAZYLOAD_ROW_START}>
      <OffsetTracking rowIdx={idx} onOffsetReached={onOffsetReached}>
        <div ref={ref}>
          {isVisible ? (
            <ItemRow
              rowAlign={rowAlign}
              rowIdx={idx}
              width={width}
              height={height}
              scrollOptims={scrollOptims}
            >
              {layoutItems.map(
                (layoutItem: TorpedoLayoutedItem<TorpedoItem>) => (
                  <TorpedoItemElement
                    key={`item-${layoutItem.idx}`}
                    layoutItem={layoutItem}
                    dataItem={dataItems[layoutItem.dto.id]}
                    renderItem={renderItem}
                  />
                )
              )}
            </ItemRow>
          ) : (
            <HiddenRow items={layoutItems} width={width} height={height} />
          )}
          {contentStrip && (
            <TorpedoContentStrip width={width}>
              {contentStrip}
            </TorpedoContentStrip>
          )}
        </div>
      </OffsetTracking>
    </LazyContext.Provider>
  );
};
TorpedoRow.displayName = 'TorpedoRow';

export default TorpedoRow;
